<template>
  <Sidebar
    :class="$style.sidebar"
    :isDisabledPrimary="invalid || disableEditing"
    :isLoadingPrimary="loading"
    :showSidebar="showSidebar"
    :title="title"
    @close="handleClose"
    @save="handleSave"
  >
    <template v-slot:body>
      <AdministratorForm
        v-model="administrator"
        :canViewRoles="canViewRoles"
        :isUpdating="isUpdating"
        :readonly="disableEditing"
      />
    </template>
  </Sidebar>
</template>

<script>
import { ItemActionConsts } from 'client/util/Constants';
import { Sidebar } from '@jumpcloud/ui-components';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import AdministratorForm from 'client/settings/AdministratorSettings/AdministratorSidebar/AdministratorForm.vue';
import MspState from 'client/components/models/MspState';

const administratorModel = 'AdministratorModel';
const returnToRoute = '/settings/administrators';
const settingsModule = 'SettingsModule';

export default {
  name: 'AdministratorSidebarController',

  components: {
    AdministratorForm,
    Sidebar,
  },

  props: {
    invalid: {
      type: Boolean,
      default: false,
    },
    itemId: {
      type: String,
      default: '',
    },
    showSidebar: {
      type: Boolean,
      default: false,
    },
    view: {
      type: String,
      default: ItemActionConsts.create,
    },
  },

  data() {
    return {
      loading: false,
    };
  },

  MspState,

  computed: {
    ...mapGetters(settingsModule, [
      'isAdminWithBilling',
    ]),

    ...mapState(settingsModule, ['roles']),

    ...mapState(administratorModel, [
      'item',
      'previousItem',
    ]),

    administrator: {
      get() {
        return this.item;
      },
      set(newValue) {
        this.updateItem(newValue);
      },
    },

    canViewRoles() {
      return this.roles.length > 0;
    },

    disableEditing() {
      return !this.isAdminWithBilling || this.item.isProvider;
    },

    isUpdating() {
      return this.view === ItemActionConsts.update;
    },

    title() {
      return this.view === ItemActionConsts.create
        ? 'Create New Administrator'
        : 'Edit Administrator';
    },
  },

  watch: {
    itemId: {
      async handler(current, previous) {
        if (current !== previous) {
          this.resetItem();
          await this.initializeItem();
        }
      },
      immediate: true,
    },

    /**
     * Reset the item when hiding the sidebar and initialize the item when showing it.
     */
    showSidebar: {
      handler(current) {
        if (current === false || this.isUpdating) {
          this.resetItem();
        } else if (current === true) {
          this.initializeItem();
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this.resetItem();
  },

  methods: {
    ...mapActions(administratorModel, [
      'fetchItem',
      'resetItem',
      'saveItem',
      'setItemStateToCreate',
      'setItemStateToUpdate',
      'updateItem',
    ]),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    beforeRouteLeave(to, from, next) {
      this.handleBeforeRouteLeave(to, from, next);
    },

    findPermission(value) {
      return this.permissions.find((role) => role.value === value).name;
    },

    handleClose() {
      this.resetItem();
      this.$router.push(returnToRoute);
    },

    async handleSave() {
      this.loading = true;

      try {
        await this.saveItem();

        this.triggerSuccessNotification({
          title: 'Saved',
          message: 'Administrator saved successfully.',
        });
        this.handleClose();
      } catch (e) {
        this.triggerErrorNotification({
          title: 'Error',
          message: e.message || 'Unable to save administrator.',
        });
      } finally {
        this.loading = false;
      }
    },

    handleUpdateActiveTab(tab) {
      this.currentTab = tab;
    },

    /**
     * Set the item state and optionally fetch the item when showing the side bar and reset it when
     * hiding it.
     * @todo The former case will be implemented in PRO-501, it's not needed when creating an item.
     * @see https://jumpcloud.atlassian.net/browse/PRO-500
     */
    async initializeItem() {
      if (this.isUpdating && this.itemId) {
        this.setItemStateToUpdate();
        await this.fetchItem(this.itemId);
      } else {
        this.resetItem();
        this.setItemStateToCreate();
      }
    },
  },
};
</script>

<style module scoped>
.sidebar.sidebar {
  padding-bottom: var(--jc-spacer-medium);
  z-index: 2;
}

.banner {
  margin: 0 var(--jc-spacer-negative) var(--jc-spacer);
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>
